<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { provideDropdownContext } from './provider.js';

const props = defineProps({
  ignore: {
    type: Array,
    default: () => [],
  },
  teleport: {
    type: Boolean,
    default: true,
  },
  preferTop: {
    type: Boolean,
    default: false,
  },
  // Show dropdown to the right of trigger, aligned at bottom
  alignRight: {
    type: Boolean,
    default: false,
  },
  // Auto-flip dropdown to avoid screen edges (specific for Switch Account)
  autoFlip: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);
const [isOpen, toggle] = useToggle(false);

const triggerRef = ref(null);
const dropdownRef = ref(null);
const dropdownPosition = ref({ top: 0, left: 0, bottom: undefined });

const closeMenu = () => {
  if (isOpen.value) {
    emit('close');
    toggle(false);
  }
};

const updateDropdownPosition = async () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  let top;
  let left;

  if (props.alignRight) {
    // Position to the right of trigger
    left = rect.right + 8; // 8px gap to the right

    if (dropdownRef.value) {
      const dropdownHeight = dropdownRef.value.offsetHeight;
      const dropdownWidth = dropdownRef.value.offsetWidth;

      if (props.preferTop) {
        // Bottom of dropdown aligns with top of trigger (always open upward)
        top = rect.top - dropdownHeight - 8;
        if (top < 16) {
          top = 16;
        }
      } else {
        // Bottom aligns with bottom of trigger, still expanding upward
        top = rect.bottom - dropdownHeight;
        if (top < 16) {
          top = 16;
        }
      }

      // If not enough space on right, show on left
      if (left + dropdownWidth > windowWidth - 16) {
        left = rect.left - dropdownWidth - 8;
      }

      // Ensure it doesn't overflow left edge
      if (left < 16) {
        left = 16;
      }
    } else {
      top = rect.bottom;
    }
  } else {
    // Default behavior: below trigger
    left = rect.left;

    // Check if dropdown would overflow bottom of viewport
    if (dropdownRef.value) {
      // Auto-flip logic (for Switch Account with autoFlip prop)
      // 🔥 FINAL FIX: Use bottom-anchored approach - NO height measurement needed!
      if (props.autoFlip) {
        const padding = 16;
        const offset = 8;
        const MIN_SPACE_BELOW = 220; // Minimum space needed to open downward

        // 🔥 Use window.innerHeight directly (not the variable from outer scope)
        const windowH = window.innerHeight;
        const windowW = window.innerWidth;

        // Calculate available space below trigger
        const spaceBelow = windowH - rect.bottom;

        // Get dropdown width for left positioning
        const dropdownWidth = dropdownRef.value?.offsetWidth || 320;

        let finalTop = null;
        let finalBottom = null;
        let finalLeft = rect.left;

        // Decision: if not enough space below, open upward using bottom-anchor
        if (spaceBelow < MIN_SPACE_BELOW) {
          // 🎯 Open UPWARD: anchor dropdown's bottom to trigger's top
          finalBottom = windowH - rect.top + offset;
          finalTop = null;
        } else {
          // Open DOWNWARD: normal behavior
          finalTop = rect.bottom + offset;
          finalBottom = null;
        }

        // Ensure dropdown doesn't overflow right edge
        if (finalLeft + dropdownWidth > windowW - padding) {
          finalLeft = windowW - dropdownWidth - padding;
        }
        if (finalLeft < padding) {
          finalLeft = padding;
        }

        dropdownPosition.value = {
          top: finalTop,
          left: finalLeft,
          bottom: finalBottom,
        };
        return;
      }

      // For non-autoFlip dropdowns, measure here
      const dropdownWidthNormal = dropdownRef.value.offsetWidth;
      const dropdownHeightNormal = dropdownRef.value.offsetHeight;

      // If preferTop is set, use bottom positioning to force upward expansion
      if (props.preferTop) {
        // 使用 bottom 定位：距离屏幕底部的距离 = 屏幕高度 - 触发器顶部 + 间隙
        const bottom = windowHeight - rect.top + 8;
        dropdownPosition.value = { top: undefined, left, bottom };

        // Ensure dropdown doesn't overflow right edge
        if (left + dropdownWidthNormal > windowWidth) {
          dropdownPosition.value.left = windowWidth - dropdownWidthNormal - 16;
        }
        if (dropdownPosition.value.left < 16) {
          dropdownPosition.value.left = 16;
        }
        return;
      }

      // Normal downward expansion
      top = rect.bottom + 8;
      const spaceBelow = windowHeight - rect.bottom - 16;
      const spaceAbove = rect.top - 16;

      // Auto-flip: if not enough space below, open upward
      if (
        spaceBelow < dropdownHeightNormal &&
        spaceAbove > dropdownHeightNormal
      ) {
        // Open upward - enough space above
        top = rect.top - dropdownHeightNormal - 8;
        if (top < 16) {
          top = 16;
        }
      } else if (spaceBelow < dropdownHeightNormal && spaceAbove > spaceBelow) {
        // Open upward - more space above than below (even if not enough)
        top = Math.max(16, rect.top - dropdownHeightNormal - 8);
      } else {
        // Open downward
        if (top + dropdownHeightNormal > windowHeight - 16) {
          top = Math.max(16, windowHeight - dropdownHeightNormal - 16);
        }
      }

      // Ensure dropdown doesn't overflow right edge
      if (left + dropdownWidthNormal > windowWidth) {
        left = windowWidth - dropdownWidthNormal - 16;
      }
      if (left < 16) {
        left = 16;
      }
    } else {
      top = rect.bottom + 8;
    }
  }

  dropdownPosition.value = { top, left, bottom: undefined };
};

// Update position when dropdown opens
watch(isOpen, async newValue => {
  if (newValue) {
    await nextTick();
    // 🔥 FIX: Use await to ensure first call completes before second
    await updateDropdownPosition();

    // Update again after a short delay to ensure dropdown is fully rendered
    setTimeout(async () => {
      await updateDropdownPosition();
    }, 80);

    // Update position on scroll and resize
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
  } else {
    window.removeEventListener('scroll', updateDropdownPosition, true);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});

// Reliable click-outside: manual document pointerdown listener.
// The vOnClickOutside directive from VueUse receives the ignore list as a
// ComputedRef<HTMLElement[]> (single object), but its API expects an
// array of MaybeElementRef – causing the ignore to silently fail and the
// dropdown to close on every click inside the teleported body.
// We implement the check ourselves instead.
const isHTMLElement = v =>
  typeof HTMLElement !== 'undefined' && v instanceof HTMLElement;

const isTargetInsideIgnored = target => {
  // Always ignore clicks inside the dropdown body itself.
  if (dropdownRef.value?.contains(target)) return true;
  // Always ignore clicks inside the trigger.
  if (triggerRef.value?.contains(target)) return true;

  for (const item of props.ignore) {
    if (!item) continue;

    if (typeof item === 'string') {
      try {
        const matches = document.querySelectorAll(item);
        for (const el of matches) {
          if (isHTMLElement(el) && el.contains(target)) return true;
        }
      } catch (_) {
        /* invalid selector */
      }
    } else if (isHTMLElement(item)) {
      if (item.contains(target)) return true;
    } else if (isHTMLElement(item?.value)) {
      if (item.value.contains(target)) return true;
    } else if (isHTMLElement(item?.$el)) {
      if (item.$el.contains(target)) return true;
    }
  }
  return false;
};

const handleDocumentPointerDown = event => {
  if (!isOpen.value) return;
  if (isTargetInsideIgnored(event.target)) return;
  closeMenu();
};

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown, true);
});

onUnmounted(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
});

const dropdownStyle = computed(() => {
  const style = {
    position: 'fixed',
    left: `${dropdownPosition.value.left}px`,
    zIndex: 9999,
  };

  // Use bottom positioning when set (for preferTop or autoFlip upward)
  // When using bottom, DO NOT set top (must be mutually exclusive)
  const bottomVal = dropdownPosition.value.bottom;
  const topVal = dropdownPosition.value.top;

  if (bottomVal !== null && bottomVal !== undefined) {
    style.bottom = `${bottomVal}px`;
    // Explicitly do NOT set top when using bottom
  } else if (topVal !== null && topVal !== undefined) {
    style.top = `${topVal}px`;
  }

  return style;
});

provideDropdownContext({
  isOpen,
  toggle,
  closeMenu,
});
</script>

<template>
  <div ref="triggerRef" class="relative space-y-2">
    <slot name="trigger" :is-open :toggle="() => toggle()" />
    <Teleport v-if="props.teleport" to="body">
      <div v-if="isOpen" ref="dropdownRef" :style="dropdownStyle">
        <slot />
      </div>
    </Teleport>
    <!-- When not teleporting, render inline without fixed positioning.
         The parent has class="relative", so we position absolutely within it. -->
    <div
      v-else-if="isOpen"
      ref="dropdownRef"
      style="
        position: absolute;
        left: 0;
        top: 100%;
        z-index: 9999;
        min-width: 100%;
      "
    >
      <slot />
    </div>
  </div>
</template>
